import React, { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { Table } from '@components/common';
import TextAvatar from '@components/common/TextAvatar';

import useQueryDashboardAssetTypesSummaryPerOrganization
	from '@hooks/dashboardPO/useQueryDashboardAssetTypesSummaryPerOrganization';

import { ISortBy, ITablePagination, ITableSearch, ITableSort } from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { getAssetTypesPerOrganizationColumns } from './utils';

import styles from './DashboardPOAssetTypePerOrganizationTable.module.scss';

interface IDashboardPOAssetTypePerOrganizationTable {
	assetTypeId: string;
	assetTypeName: string;
	assetIconId: string;
	assetSubTypeId?: string;
}

const TABLE_HEIGHT = '540px';

const DashboardPOAssetTypePerOrganizationTable = ({
	assetTypeId,
	assetTypeName,
	assetIconId,
	assetSubTypeId,
}: IDashboardPOAssetTypePerOrganizationTable) => {
	const navigate = useNavigate();

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');

	const {
		countOfRecords,
		dashboardAssetTypesPerOrganizationsSummary,
		isLoadingDashboardAssetTypesPerOrganizationsSummary,
	} = useQueryDashboardAssetTypesSummaryPerOrganization({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		id: assetTypeId,
		assetSubTypeId,
	});

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const search: ITableSearch[] = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by Organisation name',
	}], [searchValue, searchHandler]);

	const pageHandler = useCallback((pageNumber: number) => setPage(pageNumber), []);
	const pageSizeHandler = useCallback((size: number) => {
		setPage(defaultMeta.page);
		setPageSize(size);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [countOfRecords, page, pageSize, pageHandler, pageSizeHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const columns = useMemo(() => getAssetTypesPerOrganizationColumns({ navigate }), [navigate]);

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				<span className={styles.title}>Type: </span>
				<TextAvatar
					avatarId={assetIconId}
					firstName={assetTypeName}
					fontSize='10px'
					isProfileLogo
					size='24px'
				/>
				<span className={styles.assetName}>{assetTypeName}</span>
			</div>
			<Table
				columns={columns}
				data={dashboardAssetTypesPerOrganizationsSummary}
				isLoading={isLoadingDashboardAssetTypesPerOrganizationsSummary}
				isWithoutActions
				isWithoutCheckbox
				isWithoutDeleteAction
				isWithoutFilter
				maxHeight={TABLE_HEIGHT}
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
			/>
		</div>
	);
};

export default memo(DashboardPOAssetTypePerOrganizationTable);
